import { useMutation } from '@apollo/client';
import imageCompression from 'browser-image-compression';
import { useRef, useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { Button } from '../../components/button';
import { Alert, Muted, Stack } from '../../components/ui';
import { GET_PRESIGNED_URLS } from '../../services/carService/queries';
import { UPDATE_STAY_PHOTOS } from '../../services/stayService/queries';
import { toPublicS3ObjectUrl } from '../../utils/s3PublicUrl';
import type { StayListing } from './stayForm';

export const STAY_MIN_PHOTOS = 5;
const MAX_FILE_BYTES = 10 * 1024 * 1024;

const Grid = styled.div`
  ${tw`grid grid-cols-2 gap-3 sm:grid-cols-3`}
`;

const Tile = styled.figure<{ $cover?: boolean }>`
  ${tw`relative m-0 overflow-hidden`}
  aspect-ratio: 4 / 3;
  border-radius: var(--radius-card, 12px);
  background: var(--color-bg-secondary);
  ${({ $cover }) => ($cover ? tw`col-span-2 sm:col-span-3` : '')}
  ${({ $cover }) => ($cover ? 'aspect-ratio: 16 / 9;' : '')}
  img {
    ${tw`object-cover w-full h-full`}
  }
`;

const TileBadge = styled.span`
  ${tw`absolute px-2 py-1 text-xs font-semibold`}
  top: 8px;
  left: 8px;
  border-radius: 9999px;
  background: var(--color-bg-elevated, #fff);
  color: var(--color-text-primary);
  box-shadow: var(--shadow-xs);
`;

const TileActions = styled.div`
  ${tw`absolute flex gap-2`}
  right: 8px;
  bottom: 8px;
`;

const TileButton = styled.button`
  ${tw`px-2 py-1 text-xs font-medium cursor-pointer`}
  border-radius: 9999px;
  border: none;
  background: var(--color-bg-elevated, #fff);
  color: var(--color-text-primary);
  box-shadow: var(--shadow-xs);
`;

const HiddenFileInput = styled.input`
  ${tw`hidden`}
`;

async function compress(file: File): Promise<File> {
  try {
    const result = await imageCompression(file, {
      maxSizeMB: 4,
      maxWidthOrHeight: 1920,
      useWebWorker: true,
      fileType: 'image/jpeg',
    });
    return new File([result], file.name, { type: result.type });
  } catch {
    return file;
  }
}

async function putToS3(url: string, file: File): Promise<void> {
  const response = await fetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'image/jpeg' },
    body: file,
  });
  if (!response.ok) {
    throw new Error(`Upload failed (${response.status})`);
  }
}

/**
 * Photos of a stay, in order (the first is the cover). New files go to S3
 * through the listing's presigned URLs, then `updateStayPhotos` saves the
 * whole ordered list; removing or reordering saves the list as well.
 */
export function StayPhotosEditor({
  listing,
  onSaved,
}: {
  listing: Pick<StayListing, 'id' | 'images'>;
  onSaved?: (listing: StayListing) => void;
}) {
  const fileInput = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [getPresignedUrls] = useMutation(GET_PRESIGNED_URLS);
  const [updatePhotos, { loading: saving }] = useMutation(UPDATE_STAY_PHOTOS);
  const photos = (listing.images ?? []).map(image => image.imageUrl).filter(Boolean) as string[];

  const save = async (imageUrls: string[]) => {
    setError(null);
    try {
      const { data } = await updatePhotos({ variables: { id: listing.id, imageUrls } });
      if (data?.updateStayPhotos) onSaved?.(data.updateStayPhotos);
    } catch (e: any) {
      setError(e?.message ?? 'Could not save the photos');
    }
  };

  const onFiles = async (files: FileList | null) => {
    const picked = Array.from(files ?? []).filter(
      file => file.type.startsWith('image/') && file.size <= MAX_FILE_BYTES,
    );
    if (fileInput.current) fileInput.current.value = '';
    if (!picked.length) {
      setError('Choose image files of 10 MB or less');
      return;
    }
    setUploading(true);
    setError(null);
    try {
      const { data } = await getPresignedUrls({
        variables: { fileType: 'image/jpeg', carId: listing.id, count: picked.length },
      });
      const targets: { url: string; key: string }[] = data?.getPresignedUrls ?? [];
      const keys = await Promise.all(
        picked.map(async (file, index) => {
          await putToS3(targets[index].url, await compress(file));
          return targets[index].key;
        }),
      );
      await save([...photos, ...keys]);
    } catch (e: any) {
      setError(e?.message ?? 'Could not upload the photos');
    } finally {
      setUploading(false);
    }
  };

  const remove = (index: number) => save(photos.filter((_, i) => i !== index));
  const makeCover = (index: number) =>
    save([photos[index], ...photos.filter((_, i) => i !== index)]);

  return (
    <Stack $gap={4}>
      {error && (
        <Alert tone="error" title="Photos">
          {error}
        </Alert>
      )}
      <Muted $size="sm" data-testid="stay-photo-count">
        {photos.length < STAY_MIN_PHOTOS
          ? `${photos.length} of ${STAY_MIN_PHOTOS} photos. Add ${STAY_MIN_PHOTOS - photos.length} more to publish.`
          : `${photos.length} photos`}
      </Muted>
      {photos.length > 0 && (
        <Grid>
          {photos.map((photo, index) => (
            <Tile key={`${photo}-${index}`} $cover={index === 0}>
              <img
                src={toPublicS3ObjectUrl(photo)}
                alt={index === 0 ? 'Cover' : `Listing view ${index + 1}`}
                loading="lazy"
              />
              {index === 0 && <TileBadge>Cover photo</TileBadge>}
              <TileActions>
                {index > 0 && (
                  <TileButton type="button" onClick={() => makeCover(index)} disabled={saving}>
                    Make cover
                  </TileButton>
                )}
                <TileButton type="button" onClick={() => remove(index)} disabled={saving}>
                  Remove
                </TileButton>
              </TileActions>
            </Tile>
          ))}
        </Grid>
      )}
      <HiddenFileInput
        ref={fileInput}
        type="file"
        accept="image/*"
        multiple
        onChange={event => onFiles(event.target.files)}
        data-testid="stay-photo-input"
      />
      <div>
        <Button
          type="button"
          theme="secondary"
          text={uploading ? 'Uploading…' : 'Add photos'}
          isLoading={uploading}
          onClick={() => fileInput.current?.click()}
        />
      </div>
    </Stack>
  );
}
